import { doctorBffApi } from '@/modules';
import {
  EpicClinicalNotePayload,
  EpicDocument,
  EpicEncounter,
  EpicPatientSyncPayload,
  EpicSurgicalEvent
} from '@/modules/epic/epic.model';
import {
  PatientId,
  SurgicalEvent,
  WithPatientId
} from '@/modules/surgicalEvent';
import { EpicService } from '@/modules/epic/epic.service';
// Imported from the file rather than from `@/modules/billing`: the billing barrel pulls in
// `billing.slice`, which imports `@/modules/surgicalEvent` back.
import { deskPaymentStateTags } from '@/modules/billing/deskPayment.slice';
import { wrapError } from '../error';
import { ID } from '@ianesthesie-kit/services';

export const epicSlice = doctorBffApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    epicSurgicalEvent: builder.query<EpicSurgicalEvent | null, PatientId>({
      queryFn: ({ patientId }) =>
        EpicService.findEpicSurgicalEvent(patientId).catch(wrapError),
      providesTags: (result, error, { patientId }) =>
        error ? [] : [{ type: 'EpicSurgicalEvent', id: patientId }]
    }),

    epicEncounters: builder.query<EpicEncounter[], PatientId>({
      queryFn: ({ patientId }) =>
        EpicService.findEpicEncounters(patientId).catch(wrapError)
    }),

    epicDocuments: builder.query<EpicDocument[], PatientId>({
      queryFn: ({ patientId }) =>
        EpicService.findEpicDocuments(patientId).catch(wrapError),
      providesTags: (result, error, arg) =>
        error ? [] : [{ type: 'EpicDocuments', id: arg.patientId }]
    }),

    epicDocumentBase64Data: builder.query<
      string,
      WithPatientId<{ documentId: ID }>
    >({
      queryFn: ({ patientId, documentId }) =>
        EpicService.fetchEpicDocumentBase64Data(patientId, documentId).catch(
          wrapError
        ),
      providesTags: (result, error, { patientId }) =>
        error ? [] : [{ type: 'EpicDocuments', id: patientId }]
    }),

    manualEpicLink: builder.mutation<EpicSurgicalEvent, EpicPatientSyncPayload>(
      {
        queryFn: (data) => EpicService.manualLink(data).catch(wrapError),
        invalidatesTags: (result, error, { patientId }) =>
          error
            ? []
            : [
                { type: 'EpicSurgicalEvent', id: patientId },
                { type: 'Allergy', id: patientId },
                { type: 'MedicalHo', id: patientId },
                { type: 'Meds', id: patientId }
              ]
      }
    ),

    syncEpicData: builder.mutation<SurgicalEvent, PatientId>({
      queryFn: ({ patientId }) =>
        EpicService.syncData(patientId).catch(wrapError),
      invalidatesTags: (result, error, { patientId }) =>
        error
          ? []
          : [
              { type: 'EpicSurgicalEvent', id: patientId },
              { type: 'Allergy', id: patientId },
              { type: 'MedicalHo', id: patientId },
              { type: 'Meds', id: patientId },
              { type: 'SurgeryInfo', id: patientId },
              // The sync rewrites the surgery date from the EHR, which is what a card hold's
              // renewal plan is derived from — the same reason `patchSurgeryInfo` invalidates it.
              // Unconditional here: the response says what the event now is, not what moved.
              ...deskPaymentStateTags()
            ]
    }),

    createEpicClinicalNote: builder.mutation<
      void,
      WithPatientId<EpicClinicalNotePayload>
    >({
      queryFn: ({ patientId, ...payload }) =>
        EpicService.createClinicalNote(patientId, payload).catch(wrapError),
      invalidatesTags: (result, error, arg) =>
        error ? [] : [{ type: 'EpicDocuments', id: arg.patientId }]
    })
  })
});

export const {
  useEpicSurgicalEventQuery,
  useEpicEncountersQuery,
  useEpicDocumentsQuery,
  useManualEpicLinkMutation,
  useSyncEpicDataMutation,
  useCreateEpicClinicalNoteMutation
} = epicSlice;
